import { Controller } from "stimulus";
import notify from "../../packs/notify";
import debounce from 'lodash.debounce'
import i18next from 'i18next';
import i18n from "../../packs/i18n";
import axios from "axios"

const contentSelector = "[data-portal--submission-target='content']"

export default class extends Controller {
  static targets = [
    "tabs",
    "form",
    "animalAmountType",
    "submissionOriginType",
    "setupSpecimenCount",
    "setupAnimalCount",
    "noPreviousSubmit",
    "noNextSubmit",
    "content",
    // these 3 targets coming from setup page
    "setupAnimalSexCodeId",
    "autoCompleteHidden",
    "setupIncrementalId",
    "setupIncrementalName",
    "item",
  ];

  static values = {
    tabIndex: { type: Number, default: 0 },
    preSelectedCountry: { type: Number, default: 0 },
    targetLink: String,
    targetLinkType: { type: String, default: "" },
    accessionId: Number,
    autosaveDelay: Number,
    currentUserId: Number,
    checkMSInputs: { type: Boolean, default: false },
    currentUserLocale: String,
    isTabChange: { type: Boolean, default: false },
    isDebounce: { type: Boolean, default: false },
    setupTaxonId: {Number, default: 0},
    setupTaxonLabel: String,
    submissionOriginType: String,
    submissionTypeMap: {
      type: Object,
      default: {
        product: i18next.t("Non-Animals"),
        animal: i18next.t("Animals"),
      },
    },
  };

  initialize (){
    this.submitForm = this.submitForm.bind(this)
  }

  connect() {
    this.boundHandleFrame = this.handleFrame.bind(this)
    this.element.addEventListener("turbo:frame-render", this.boundHandleFrame)

    this.boundCheckAnimalsCount = this.checkAnimalsCount.bind(this)
    document.addEventListener("portal--submission-animal:triggerAnimalCountProcess", this.boundCheckAnimalsCount)
    this.checkAndSetLanguage()
  }

  checkAndSetLanguage(){
    i18n.then(() => {
      i18next.changeLanguage(this.currentUserLocaleValue, (err, t) => {
        if (err) return console.error('something went wrong loading i18 locale', err);
      });
    })
  }

  disconnect(){
    this.element.removeEventListener("turbo:frame-render", this.boundHandleFrame)
    document.removeEventListener("autocomplete:triggerAutocompleteChangeEvent", this.boundSetTaxonValueAndId)
    this.setValuesForProgressiveButtons()
  }

  handlePrevious(event) {
    this.setPrevents(event);
    this.generateTargetUrl(event);
    this.setValuesForProgressiveButtons()
    this.tabIndexValue--;
    if (!this.hasNoPreviousSubmitTarget && this.hasFormTarget){
      this.submitForm(event)
      .then(res => {
        return this.prepareVisitUrl(res)
      })
      .catch(() => "");
    }else{
      this.visitUrl(this.targetLinkValue, "submission_form_tab");
    }
  }

  handleNext(event) {
    this.setPrevents(event);
    this.generateTargetUrl(event);
    this.tabIndexValue++;
    this.setValuesForProgressiveButtons()
    if ( ( !this.hasNoNextSubmitTarget || !this.hasNoPreviousSubmitTarget) && this.hasFormTarget){
      this.submitForm(event)
      .then(res => {
        return this.prepareVisitUrl(res)
      })
      .catch(() => "");
    } else {
      this.visitUrl(this.targetLinkValue, "submission_form_tab");
    }
  }

  setValuesForProgressiveButtons(){
    this.isTabChangeValue = false
    this.isDebounceValue = false
  }

  prepareVisitUrl(res){
    if (res.status >= 200 && res.status <= 299) {
      this.visitUrl(this.targetLinkValue, "submission_form_tab");
    } else {
      notify({
        message: i18next.t('Something Went Wrong')  + " : " + res,
        status: "error",
      });
    }
  }

  autosave(event){
    if (!this.validateForm(true)) {
      event.preventDefault();
      return
    }else{
      if (this.autosaveDelayValue > 0) {
        this.isDebounceValue = true
        debounce(this.submitForm(event), this.autosaveDelayValue)
        // TODO - We can add after by one by saved specimen data for V2
        // document.dispatchEvent(new CustomEvent("occuredAutoSave", {detail: {event: event}}))
        // TODO - some tabs needs load own data after autosave specifially additional_data
        // this.visitUrl(this.targetLinkValue, "submission_form_tab");
      }
    }
  }

  tabChange(e){
    if (!this.hasNoPreviousSubmitTarget && this.hasFormTarget){
      this.isTabChangeValue = true
      this.isDebounceValue = true
      if (this.validateForm(true)) {
        if (this.autosaveDelayValue > 0) {
          debounce(this.submitForm(e), this.autosaveDelayValue)
        }
      }
    }
  }

  generateTargetUrl(event) {
    const params = new URLSearchParams();
    params.append("id", parseInt(this.accessionIdValue));
    if (this.targetLinkTypeValue) {
      params.append("target_type", this.targetLinkTypeValue);
    }

    if (this.hasSubmissionOriginTypeTarget){
      let item = ""
      if (this.submissionOriginTypeTarget.type == 'hidden'){
        item = this.submissionOriginTypeTarget
      } else { 
        item = this.findCheckedValue(this.submissionOriginTypeTargets)
      }
      params.append("submission_origin_type", item.value);
    }

    if (this.hasSetupSpecimenCountTarget){
      params.append("setup_specimen_count", parseInt(this.setupSpecimenCountTarget.value));
    }

    if (this.hasSetupAnimalCountTarget){
      params.append("setup_animal_count", parseInt(this.setupAnimalCountTarget.value));
    }

    if (this.hasSetupAnimalSexCodeIdTarget ){
      if ( this.setupAnimalSexCodeIdTarget.value) params.append("setup_animal_sex_code_id", this.setupAnimalSexCodeIdTarget.value);
    }

    if (this.hasAutoCompleteHiddenTarget){
      if (this.autoCompleteHiddenTarget.value) params.append("setup_animal_breed_id", this.autoCompleteHiddenTarget.value);
    }

    if (this.hasSetupIncrementalIdTarget){
      if (this.setupIncrementalIdTarget.value) params.append("setup_incremental_id", this.setupIncrementalIdTarget.value);
    }

    if (this.hasSetupIncrementalNameTarget){
      if (this.setupIncrementalNameTarget.value) params.append("setup_incremental_name", this.setupIncrementalNameTarget.value);
    }

    const submissionSending = event.currentTarget.getAttribute("data-submission-sending")
    if (submissionSending){
      params.append("submission_sent", "success");
    }

    // if we have checkMSInputs mark in progress button page like handleNext or Previous buttons
    //  we will need to check extra input validation for Test page specimen records
    const checkMSInputs = event.currentTarget.getAttribute("data-checkMSInputs")
    this.checkMSInputsValue = checkMSInputs ? true : false
    const url = event.currentTarget.getAttribute("data-target-link");
    this.targetLinkValue = url + "?" + params.toString();
  }

  findCheckedValue(targets){
    return targets.find(
      (elem) => (elem.checked)
    )
  }

  setPrevents(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Should be button pointer event
  submitForm(event) {
    const formElem = this.formTarget;
    if (!this.isTabChangeValue) {
      if (!this.validateForm()) {
        event.preventDefault();
        const msg = i18next.t('Please complete the required fields')
        notify({
          message: msg,
          status: "error",
        });
        return Promise.reject(new Error(msg))
      }
    }

    let formData = new FormData(formElem);
    let owner_id = document.querySelector("#owner_owner_id");
    if (owner_id) {
      formData.append("owner_id", owner_id.value);
    }

    formData.append("current_user_id", this.currentUserIdValue)
    if (this.isDebounceValue){
      this.fetchForm(formElem.action, formData)
      return Function();
    } else {
      return this.fetchForm(formElem.action, formData)
    }
  }

  async fetchForm(url, formData){
    return fetch(url, {
      method: "PATCH",
      // TODO: (cjarvis) - Move fetch to use axios requests so we can use the same headers
      headers: { Accept: "application/json", "X-CSRF-Token": axios.defaults.headers.common['X-CSRF-Token'] },
      body: formData,
    })
    .then((response) => {
      if (!response.ok) {
        return response.text()
      } else{
        return response
      }
    })
    .then(function(resData) {
      return resData
    })
    .catch(function (err) {
      notify({
        message: i18next.t('Something Went Wrong') + " : " + err,
        status: "error",
      });
    });
  }

  validateForm(isAutoSave=false) {
    let isValid = true;
    // Tell the browser to find any required fields
    let requiredFieldSelectors = "textarea:required, input:required, select:required, [data-valid='false']";
    let requiredFields = this.formTarget.querySelectorAll(requiredFieldSelectors);
    let firstfield = ""
    for (let index = 0; index < requiredFields.length; index++) {
      const field = requiredFields[index];
      const isChecked = field.type == 'checkbox' ? field.checked : true
      let dataValid = true
      if (field.getAttribute("data-valid")) {
        dataValid = field.getAttribute("data-valid") === "true"
      }
      // For each required field, check to see if the value is empty
      // if so, we focus the only first field to attention the user start from first required field on the screen and set our value to false
      if ( !field.disabled && !field.value.trim() || !isChecked || !dataValid) {

        // if the current page in test page we need to check Specimen Multi Select container search value is valid.
        // if is not valid , we need to change classList
        if (this.checkMSInputsValue){
          const msItem = document.querySelector("input:not([style])[data-multiselect-target='search']")
          msItem ? msItem.classList.add("required-field") : ""
        }

        isValid = false;
        if (isAutoSave){
          return isValid
        }
        field.classList.add("required-field")
        if (!firstfield)
          firstfield = field
      } else {
        field.classList.remove("required-field")
      }
    };
    firstfield ? firstfield.focus() : ""
    return isValid;
  }

  visitUrl(url, frame) {
    Turbo.visit(url.toString(), { frame: frame });
  }

  handleFrame(event) {
    const content = document.querySelector(contentSelector)
    if (!content) return
    this.tabIndexValue = parseInt(content.dataset.tabindex)
    this.toggleTabs(this.tabIndexValue);
  }

  getDataTabIndex(elem) {
    return parseInt(elem.getAttribute("data-tabIndex"));
  }

  toggleTabs(tabIndexValue) {
    this.tabsTargets.forEach(function (tabElem, i) {
      if (i === tabIndexValue) {
        tabElem.classList.remove("btn-light");
        tabElem.classList.add("btn-primary");
      } else {
        tabElem.classList.remove("btn-primary");
        tabElem.classList.add("btn-light");
      }
    });
  }

  animalTabNameToggle(e){
    let value = ""
    if (e.type === 'turbo:frame-render'){
      if (!this.submissionOriginTypeValue){
        value = ""
      } else {
        value = this.submissionTypeMapValue[e.target.value]
        this.submissionOriginTypeValue = value
      }
    } else {
      value = this.submissionTypeMapValue[e.target.value]
      this.submissionOriginTypeValue = value
    }

    if (!value) value = this.submissionTypeMapValue['animal']

    this.getElement('animalTabName').innerHTML = value
    this.toggleSetupSubmissionTypeDecision(value)

    const specimenEachSubmission = this.getElement('specimenEachSubmissionTypeText')
    if (specimenEachSubmission) {
      const i18Object = { submissionOriginType: value }
      specimenEachSubmission.innerHTML = i18next.t("specimens to each submissionOriginType", i18Object)
    }

    const setupAnimalCountSubmissionType = this.getElement('setupAnimalCountSubmissionType')
    if (setupAnimalCountSubmissionType) {
      setupAnimalCountSubmissionType.innerHTML = i18next.t(value)
    }
  }

  toggleAmountOfAnimal(e){
    if (!this.hasSetupAnimalCountTarget) return
    const animalCount = parseInt(this.setupAnimalCountTarget.value)
    const item = this.getElement('specimen_main_tab_id')
    if (!animalCount && !item) return
    item.hidden = !(animalCount > 1)
  }

  toggleSetupSubmissionTypeDecision(submission_type){
    submission_type = submission_type.toLowerCase()
    const sex_element =  this.getElement('setup_animal_sex_code_decision_id')
    const breed_item = this.getElement('setup_animal_breed_decision_id')
    if (sex_element) sex_element.hidden = submission_type === "animals" ? false : true
    if (breed_item) breed_item.hidden = submission_type === "animals" ? false : true
  }

  getElement(id){
    return document.getElementById(id)
  }

  checkAnimalsCount(event){
    if (!event) return

    const animalCount = event.detail.itemCount
    const item = this.getElement('specimen_main_tab_id')
    if (!animalCount || !item) return

    item.hidden = !(animalCount > 1)
  }
}
